import { Injectable, OnDestroy } from '@angular/core';
import { MsalBroadcastService, MsalGuardConfiguration, MsalInterceptorConfiguration, MsalService } from '@azure/msal-angular';
import { AuthenticationResult, BrowserCacheLocation, EventMessage, EventType, InteractionStatus, InteractionType, IPublicClientApplication, LogLevel, PublicClientApplication } from '@azure/msal-browser';
import { BehaviorSubject, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {
  private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  isAuthenticated$ = this.isAuthenticatedSubject.asObservable();
  private _destroying$ = new Subject<void>();
  private msalInstance: IPublicClientApplication;

  constructor(private msalService: MsalService, private msalBroadcastService: MsalBroadcastService) {
    this.monitorAuthentication();
    this.msalInstance = this.createMsalInstance();
  }

  private createMsalInstance(): IPublicClientApplication {
    return new PublicClientApplication({
      auth: {
        clientId: environment.clientId,
        authority: `https://login.microsoftonline.com/${environment.tenantId}`,
        redirectUri: window.location.origin,
      },
      cache: {
        cacheLocation: BrowserCacheLocation.LocalStorage,
        storeAuthStateInCookie: this.isIE(),
      },
      system: {
        loggerOptions: {
          logLevel: LogLevel.Verbose,
          loggerCallback: (level, message, containsPii) => {
            if (!containsPii) {
              console.log(`MSAL Log [${LogLevel[level]}]: ${message}`);
            }
          },
        },
      },
    });
  }

  getMsalInterceptorConfig(): MsalInterceptorConfiguration {
    const protectedResourceMap = new Map<string, Array<string>>();
    protectedResourceMap.set('https://graph.microsoft.com/v1.0/', ['user.read']);
    protectedResourceMap.set('https://graph.microsoft.com/v1.0/users', ['user.read']);
    // protectedResourceMap.set(this.configService.getConfig('gatewayCloudURL'), [this.configService.getConfig('scope')]);

    return {
      interactionType: InteractionType.Redirect,
      protectedResourceMap,
    };
  }

  getMsalGuardConfig(): MsalGuardConfiguration {
    return {
      interactionType: InteractionType.Redirect,
      authRequest: {
        scopes: ['user.read', 'email', 'offline_access', 'openid', 'profile'],
      },
    };
  }

  private isIE(): boolean {
    return /MSIE|Trident/.test(window.navigator.userAgent);
  }

  getMsalInstance(): IPublicClientApplication {
    return this.msalInstance;
  }

  private monitorAuthentication(): void {
    // Check if user is logged in when interaction status is none
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        const accounts = this.msalService.instance.getAllAccounts();
        if (!accounts || accounts.length === 0) {
          // No accounts found, initiate login
          this.signIn();
        } else {
          // User is already authenticated
          this.isAuthenticatedSubject.next(true);
        }
      });

    // Listen for successful logins
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS),
        takeUntil(this._destroying$)
      )
      .subscribe((result: EventMessage) => {
        const payload = result.payload as AuthenticationResult;
        this.msalService.instance.setActiveAccount(payload.account);
        this.isAuthenticatedSubject.next(true);
      });
  }

  async signIn(): Promise<void> {
    try {
      await this.msalService.loginRedirect();
    } catch (error) {
      console.error('Login failed', error);
    }
  }

  isLoggedIn(): boolean {
    return this.msalService.instance.getAllAccounts().length > 0;
  }

  async getToken(): Promise<string | null> {
    const accounts = this.msalService.instance.getAllAccounts();
    if (accounts.length > 0) {
      try {
        const result = await this.msalService.instance.acquireTokenSilent({
          scopes: ['User.Read'],
          account: accounts[0],
        });
        return result.accessToken;
      } catch (error) {
        console.error('Token acquisition failed', error);
        return null;
      }
    }
    return null;
  }

  ngOnDestroy(): void {
    this._destroying$.next();
    this._destroying$.complete();
  }



}
